import { useEffect, useRef, useState, type FocusEvent, type ReactNode } from 'react'
import { useIsMemberMobile } from '../../member/useIsMemberMobile'
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight'
import './keyboardDock.css'

/**
 * Snapshot handed to a render-prop `children` function: whether the dock is
 * currently pinned above the keyboard, and which named region (tagged via
 * `data-keyboard-dock-region="..."` on a descendant) currently holds focus.
 * `focusedRegion` is `null` when nothing tagged is focused (including
 * whenever `isDocked` is false).
 */
export type KeyboardDockRenderState = {
  isDocked: boolean
  focusedRegion: string | null
}

export type KeyboardDockProps = {
  children: ReactNode | ((state: KeyboardDockRenderState) => ReactNode)
  /**
   * Header text shown above the children when docked (keyboard is open and
   * focus is inside the wrapper). Pass an already-resolved string — do not
   * rely on this component to call t() so callers control the NLS context.
   */
  label: string
  className?: string
}

/**
 * Shared reusable dock that pins its children above the soft keyboard whenever
 * focus lands inside and the keyboard is open.
 *
 * Responsibilities:
 *  1. Position: fixed above the keyboard when docked; relative in normal flow.
 *  2. Publish its measured height to `--keyboard-dock-height` on :root.
 *  3. Set `body[data-keyboard-dock="true"]` while docked (cleared on undock
 *     and unmount).  Layout rules key off this attribute.
 *
 * Deliberately does NOT import firebase/* or any service layer.
 * All document/window access is guarded for SSR.
 */
export const KeyboardDock = ({ children, label, className }: KeyboardDockProps) => {
  const isMobile = useIsMemberMobile()
  const keyboardHeight = useKeyboardHeight()
  const [isFocused, setIsFocused] = useState(false)
  const [focusedRegion, setFocusedRegion] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const isDocked = isMobile && isFocused && keyboardHeight > 0

  // — focus tracking (mirrors FloatingInput's approach) —
  const handleFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target
    if (
      !(target instanceof Node) ||
      !wrapperRef.current?.contains(target)
    ) {
      return
    }
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      setIsFocused(true)
      const regionEl = target.closest<HTMLElement>('[data-keyboard-dock-region]')
      setFocusedRegion(regionEl?.dataset.keyboardDockRegion ?? null)
    }
  }

  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    // Stay focused (and keep whichever region was focused) if the next
    // active element is still inside this wrapper — e.g. tapping a button
    // in the same dock, which blurs the field without leaving the dock.
    const nextTarget = event.relatedTarget
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return
    }
    setIsFocused(false)
    setFocusedRegion(null)
  }

  // — publish dock height to --keyboard-dock-height on :root —
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!isDocked) {
      document.documentElement.style.setProperty('--keyboard-dock-height', '0px')
      return
    }
    const el = wrapperRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
        if (typeof h === 'number') {
          document.documentElement.style.setProperty('--keyboard-dock-height', `${Math.ceil(h)}px`)
        }
      }
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.setProperty('--keyboard-dock-height', '0px')
    }
  }, [isDocked])

  // — signal dock active via body attribute —
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (isDocked) {
      document.body.dataset.keyboardDock = 'true'
    } else {
      delete document.body.dataset.keyboardDock
    }
    return () => {
      if (typeof document !== 'undefined') {
        delete document.body.dataset.keyboardDock
      }
    }
  }, [isDocked])

  // — cleanup on unmount —
  useEffect(() => {
    return () => {
      if (typeof document === 'undefined') return
      document.documentElement.style.setProperty('--keyboard-dock-height', '0px')
      delete document.body.dataset.keyboardDock
    }
  }, [])

  const renderChildren = (state: KeyboardDockRenderState) =>
    typeof children === 'function' ? children(state) : children

  if (!isMobile) {
    return <div className={className}>{renderChildren({ isDocked: false, focusedRegion: null })}</div>
  }

  const combinedClass = [
    'keyboardDock',
    isDocked ? 'keyboardDock--docked' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={wrapperRef}
      className={combinedClass}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      {isDocked ? <span className="keyboardDock__label">{label}</span> : null}
      {renderChildren({ isDocked, focusedRegion })}
    </div>
  )
}
